import { ReactElement, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import {
  ChevronLeftIcon,
  WalletIcon,
  CalendarIcon,
  EnvelopeClosedIcon,
  PhoneIcon,
  PersonIcon,
  Buildings2Icon,
  InformationSquareIcon
} from '@navikt/aksel-icons';
import classes from './LeadDetailPage.module.css';
import { Routes } from '../../../enums/Routes';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { Alert } from '../../../components/Alert/Alert';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { StatsCardsGrid, type StatsCardConfig } from '../../../components/StatsCard/StatsCard';
import { formatNorwegianDate } from '../../../utils/dateFormatters';
import { formatNOK } from '../../../utils/currencyFormatters';
import { useGetLeadDetail, useUpdateLead, useConvertLead, useAddLeadActivity, useDeleteLead, type LeadStatus } from '../../../hooks/leads/useLeads';
import { LEAD_STATUS_LABELS } from '../../../constants/lead-status';
import { SelectField } from '../../../components/SelectField/SelectField';
import { Button } from '../../../components/Button/Button';

export const LeadDetailPage = (): ReactElement => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const { data: lead, isLoading, error } = useGetLeadDetail(Number(leadId));
  const updateLeadMutation = useUpdateLead();
  const convertLeadMutation = useConvertLead();
  const addActivityMutation = useAddLeadActivity();
  const deleteLeadMutation = useDeleteLead();

  const [newNote, setNewNote] = useState('');

  // Derived state - Extract Variable pattern (Refactoring 2nd ed, p.119)
  const noteActivities = useMemo(
    () =>
      lead?.activities
        ?.filter((a) => a.type === 'NOTE')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) ?? [],
    [lead?.activities]
  );

  const systemActivities = useMemo(
    () => lead?.activities?.filter((a) => a.type !== 'NOTE') ?? [],
    [lead?.activities]
  );

  const handleStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value as LeadStatus;

    if (!newStatus || !lead || newStatus === lead.status) return;

    try {
      await updateLeadMutation.mutateAsync({
        leadId: lead.id,
        payload: { status: newStatus },
      });
      toast.success('Status oppdatert');
    } catch (error) {
      toast.error('Kunne ikke oppdatere status');
    }
  };

  const handleConvertToCustomer = async () => {
    if (!lead) return;

    if (!window.confirm(`Er du sikker på at du vil konvertere "${lead.companyName}" til kunde?`)) {
      return;
    }

    try {
      const result = await convertLeadMutation.mutateAsync({
        leadId: lead.id,
      });
      toast.success('Lead konvertert til kunde');
      navigate(`/partner-admin/customers/${result.tenantId}`);
    } catch (error) {
      toast.error('Kunne ikke konvertere lead til kunde');
    }
  };

  const handleAddNote = async () => {
    if (!lead || !newNote.trim()) return;

    try {
      await addActivityMutation.mutateAsync({
        leadId: lead.id,
        payload: {
          type: 'NOTE',
          description: newNote.trim(),
        },
      });
      toast.success('Notat lagt til');
      setNewNote('');
    } catch (error) {
      toast.error('Kunne ikke legge til notat');
    }
  };

  const handleDeleteLead = async () => {
    if (!lead) return;

    const message = lead.convertedToTenantId
      ? `Er du sikker på at du vil slette leaden "${lead.companyName}"?\n\nDenne leaden er allerede konvertert til kunde. Kunde-dataen vil ikke bli påvirket.`
      : `Er du sikker på at du vil slette leaden "${lead.companyName}"?\n\nDenne handlingen kan ikke angres.`;

    if (!window.confirm(message)) {
      return;
    }

    try {
      await deleteLeadMutation.mutateAsync(lead.id);
      toast.success('Lead slettet');
      navigate(Routes.PartnerAdminLeads);
    } catch (error) {
      toast.error('Kunne ikke slette lead');
    }
  };

  if (isLoading) {
    return (
      <div className={classes.container}>
        <GhostLoader />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className={classes.container}>
        <Alert type="error" title="Feil ved lasting av lead">
          {error ? 'Kunne ikke laste lead. Prøv igjen senere.' : 'Lead ikke funnet.'}
        </Alert>
      </div>
    );
  }

  const statsCards: StatsCardConfig[] = [
    {
      title: 'Månedlig verdi',
      value: formatNOK(lead.estimatedMonthlyValueNOK),
      subtitle: 'Estimert',
      icon: <WalletIcon />,
    },
    {
      title: 'Årlig verdi',
      value: formatNOK(lead.estimatedYearlyValueNOK),
      subtitle: 'Estimert',
      icon: <WalletIcon />,
    },
    {
      title: 'Månedlig cashback',
      value: formatNOK(lead.estimatedMonthlyCashbackNOK),
      subtitle: 'Estimert',
      icon: <WalletIcon />,
    },
    {
      title: 'Årlig cashback',
      value: formatNOK(lead.estimatedYearlyCashbackNOK),
      subtitle: 'Estimert',
      icon: <WalletIcon />,
    },
  ];

  return (
    <div className={classes.container}>
      <Link to={Routes.PartnerAdminLeads} className={classes.backLink}>
        <ChevronLeftIcon />
        Tilbake til leads
      </Link>

      <div className={classes.header}>
        <div className={classes.headerInfo}>
          <Heading as="h1">{lead.companyName}</Heading>
          {lead.convertedToTenantId === null ? (
            <div className={classes.statusRow}>
              <div className={classes.statusSelect}>
                <SelectField
                  name="status"
                  label="Status"
                  value={lead.status}
                  onChange={handleStatusChange}
                  options={Object.entries(LEAD_STATUS_LABELS).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </div>
              <div className={classes.actionsRow}>
                <Button
                  variant="primary"
                  size="medium"
                  onClick={handleConvertToCustomer}
                  disabled={convertLeadMutation.isPending}
                >
                  {convertLeadMutation.isPending ? 'Konverterer...' : 'Konverter til kunde'}
                </Button>
                <Button
                  variant="danger"
                  size="medium"
                  onClick={handleDeleteLead}
                  disabled={deleteLeadMutation.isPending}
                >
                  {deleteLeadMutation.isPending ? 'Sletter...' : 'Slett lead'}
                </Button>
              </div>
            </div>
          ) : (
            <div className={classes.statusRow}>
              <Paragraph size="lg" className={classes.subtitle}>
                Status: <span className={classes.statusBadge}>{LEAD_STATUS_LABELS[lead.status]}</span> · ✓ Konvertert til kunde
              </Paragraph>
              <div className={classes.actionsRow}>
                <Button
                  variant="danger"
                  size="medium"
                  onClick={handleDeleteLead}
                  disabled={deleteLeadMutation.isPending}
                >
                  {deleteLeadMutation.isPending ? 'Sletter...' : 'Slett lead'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <StatsCardsGrid cards={statsCards} columns={4} />

      <div className={classes.mainContent}>
        {/* Two column grid for compact info cards */}
        <div className={classes.twoColumnGrid}>
          <ContainerCard>
            <ContainerCard.Title>Kontaktinformasjon</ContainerCard.Title>
            <ContainerCard.Body>
              <div className={classes.infoList}>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>
                    <Buildings2Icon className={classes.infoIcon} />
                    Firmanavn
                  </span>
                  <span className={classes.infoValue}>{lead.companyName}</span>
                </div>
                {lead.contactPersonName && (
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>
                      <PersonIcon className={classes.infoIcon} />
                      Kontaktperson
                    </span>
                    <span className={classes.infoValue}>{lead.contactPersonName}</span>
                  </div>
                )}
                {lead.contactEmail && (
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>
                      <EnvelopeClosedIcon className={classes.infoIcon} />
                      E-post
                    </span>
                    <span className={classes.infoValue}>
                      <a href={`mailto:${lead.contactEmail}`} className={classes.link}>{lead.contactEmail}</a>
                    </span>
                  </div>
                )}
                {lead.contactPhone && (
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>
                      <PhoneIcon className={classes.infoIcon} />
                      Telefon
                    </span>
                    <span className={classes.infoValue}>
                      <a href={`tel:${lead.contactPhone}`} className={classes.link}>{lead.contactPhone}</a>
                    </span>
                  </div>
                )}
                {lead.source && (
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>
                      <InformationSquareIcon className={classes.infoIcon} />
                      Kilde
                    </span>
                    <span className={classes.infoValue}>{lead.source}</span>
                  </div>
                )}
              </div>
            </ContainerCard.Body>
            {lead.convertedToTenantId === null && (
              <ContainerCard.Footer>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => navigate(`/partner-admin/leads/${lead.id}/edit`)}
                >
                  Rediger
                </Button>
              </ContainerCard.Footer>
            )}
          </ContainerCard>

          {(lead.estimatedPricePerSeat || lead.estimatedSeats || lead.estimatedBillingPeriod) && (
            <ContainerCard>
              <ContainerCard.Title>Pris & Lisenser</ContainerCard.Title>
              <ContainerCard.Body>
                <div className={classes.infoList}>
                  {lead.estimatedPricePerSeat && (
                    <div className={classes.infoRow}>
                      <span className={classes.infoLabel}>
                        <WalletIcon className={classes.infoIcon} />
                        Pris per lisens
                      </span>
                      <span className={classes.infoValue}>{formatNOK(lead.estimatedPricePerSeat / 100)}/mnd</span>
                    </div>
                  )}
                  {lead.estimatedSeats && (
                    <div className={classes.infoRow}>
                      <span className={classes.infoLabel}>Antall lisenser</span>
                      <span className={classes.infoValue}>{lead.estimatedSeats} seter</span>
                    </div>
                  )}
                  {lead.estimatedBillingPeriod && (
                    <div className={classes.infoRow}>
                      <span className={classes.infoLabel}>Fakturering</span>
                      <span className={classes.infoValue}>
                        {lead.estimatedBillingPeriod === 'monthly' ? 'Månedlig' : 'Årlig'}
                      </span>
                    </div>
                  )}
                </div>
              </ContainerCard.Body>
              {lead.convertedToTenantId === null && (
                <ContainerCard.Footer>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => navigate(`/partner-admin/leads/${lead.id}/edit`)}
                  >
                    Rediger
                  </Button>
                </ContainerCard.Footer>
              )}
            </ContainerCard>
          )}

          {lead.nextFollowUpDate && (
            <ContainerCard>
              <ContainerCard.Title>Oppfølging</ContainerCard.Title>
              <ContainerCard.Body>
                <div className={classes.followUpCard}>
                  <div className={classes.followUpIcon}>
                    <CalendarIcon />
                  </div>
                  <div className={classes.followUpContent}>
                    <div className={classes.followUpDate}>
                      {formatNorwegianDate(lead.nextFollowUpDate)}
                    </div>
                    {lead.daysUntilFollowUp !== null && (
                      <div className={`${classes.followUpBadge} ${
                        lead.daysUntilFollowUp < 0 ? classes.overdue :
                        lead.daysUntilFollowUp === 0 ? classes.today :
                        classes.upcoming
                      }`}>
                        {lead.daysUntilFollowUp === 0
                          ? 'I dag'
                          : lead.daysUntilFollowUp < 0
                          ? `${Math.abs(lead.daysUntilFollowUp)} dager forsinket`
                          : `Om ${lead.daysUntilFollowUp} dager`}
                      </div>
                    )}
                  </div>
                </div>
              </ContainerCard.Body>
              {lead.convertedToTenantId === null && (
                <ContainerCard.Footer>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => navigate(`/partner-admin/leads/${lead.id}/edit`)}
                  >
                    Rediger
                  </Button>
                </ContainerCard.Footer>
              )}
            </ContainerCard>
          )}
        </div>

        {/* Full width cards for detailed content */}
        <ContainerCard>
            <ContainerCard.Title>Notater og samtalelogg</ContainerCard.Title>
            <ContainerCard.Body>
              {/* Add new note */}
              <div className={classes.addNoteSection}>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Legg til nytt notat (f.eks. om samtale, møte, e-post...)"
                  className={classes.noteInput}
                  rows={3}
                />
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleAddNote}
                  disabled={!newNote.trim() || addActivityMutation.isPending}
                >
                  {addActivityMutation.isPending ? 'Legger til...' : 'Legg til notat'}
                </Button>
              </div>

              {/* Display existing notes - Decompose Conditional pattern */}
              {noteActivities.length > 0 && (
                <div className={classes.notesTimeline}>
                  {noteActivities.map((activity) => (
                    <div key={activity.id} className={classes.noteItem}>
                      <div className={classes.noteHeader}>
                        <span className={classes.noteAuthor}>{activity.userName}</span>
                        <span className={classes.noteDate}>
                          {formatNorwegianDate(activity.createdAt)}
                        </span>
                      </div>
                      <div className={classes.noteContent}>{activity.description}</div>
                    </div>
                  ))}
                </div>
              )}

              {noteActivities.length === 0 && (
                <Paragraph size="sm" className={classes.emptyNotes}>
                  Ingen notater ennå. Legg til ditt første notat ovenfor.
                </Paragraph>
              )}
            </ContainerCard.Body>
        </ContainerCard>

        {systemActivities.length > 0 && (
          <ContainerCard>
              <ContainerCard.Title>Aktivitetslogg</ContainerCard.Title>
              <ContainerCard.Body>
                <div className={classes.activities}>
                  {systemActivities.map((activity) => (
                    <div key={activity.id} className={classes.activity}>
                      <div className={classes.activityHeader}>
                        <span className={classes.activityType}>{activity.type}</span>
                        <span className={classes.activityDate}>
                          {formatNorwegianDate(activity.createdAt)}
                        </span>
                      </div>
                      <div className={classes.activityDescription}>{activity.description}</div>
                      <div className={classes.activityUser}>
                        {activity.userName}
                      </div>
                    </div>
                  ))}
                </div>
              </ContainerCard.Body>
          </ContainerCard>
        )}
      </div>
    </div>
  );
};
